import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import  API_BASE_URL from '../config/baseUrl';

const CompletedCasinoPlusMinus = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken } = useSelector((state) => state.auth);
  
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totals, setTotals] = useState({
    casinoAmt: 0,
    casinoComm: 0,
    totalAmount: 0,
    myShare: 0,
    netAmount: 0
  });

  // Drilldown state - tracks navigation history
  const [drilldownHistory, setDrilldownHistory] = useState([]);
  const [currentUserId, setCurrentUserId] = useState("");
  const [currentUserName, setCurrentUserName] = useState("");

  useEffect(() => {
    if (location.state) {
      fetchPlusMinusData();
    }
  }, [location.state, currentUserId]);

  const fetchPlusMinusData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { userId, date, casinoId } = location.state;
      
      // Use currentUserId if drilling down, otherwise use the userId from location.state
      const targetUserId = currentUserId || userId;
      
      const response = await fetch(`${API_BASE_URL}/ant-pro/get-completed-casino-plusminus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          userId: targetUserId,
          date: date,
          casinoId: casinoId?.toString()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const result = await response.json();
      
      if (result.status && result.data) {
        setReportData(result.data);
        
        // Calculate totals
        const calculatedTotals = result.data.reduce((acc, item) => ({
          casinoAmt: acc.casinoAmt + (item.amount || 0),
          casinoComm: acc.casinoComm + (item.commission || 0),
          totalAmount: acc.totalAmount + (item.total || 0),
          myShare: acc.myShare + (item.myShare || 0),
          netAmount: acc.netAmount + (item.netAmount || 0)
        }), {
          casinoAmt: 0,
          casinoComm: 0,
          totalAmount: 0,
          myShare: 0,
          netAmount: 0
        });
        
        setTotals(calculatedTotals);
      } else {
        setError(result.message || 'Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching plus minus data:', error);
      setError(error.message || 'An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  };

  // Handle clicking on a user code to drill down
  const handleUserClick = function(userId, userName) {
    // Check if this user is a Client (starts with 'C')
    // Clients are the bottom level, so we don't drill down further
    const isClient = userId && userId.startsWith('C');
    
    if (isClient) {
      // At Client level - do nothing
      return;
    }
    
    // Add current state to history before drilling down
    setDrilldownHistory([...drilldownHistory, { userId: currentUserId, userName: currentUserName }]);
    setCurrentUserId(userId);
    setCurrentUserName(userName);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const formatNumber = (num) => {
    return num?.toFixed(2) || '0.00';
  };

  return (
    <main className="bg-white min-h-screen p-4">
      <div className="max-w-7xl mx-auto">
        <div className="border border-gray-200">
          {/* Header */}
          <div className="bg-gray-700 px-4 py-3 flex items-center justify-between">
            <div>
              <p className="text-white text-xl font-normal">Company Report</p>
              {/* Show current drilldown level */}
              {currentUserName && (
                <p className="text-sm text-gray-200 mt-1">
                  Showing downline for: <span className="font-semibold">{currentUserName}</span> ({currentUserId})
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleBack}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded transition-colors"
              >
                Close
              </button>
            </div>
          </div>

          {/* Event Info */}
          {location.state && (
            <div className="bg-gray-100 px-4 py-2 text-sm">
              <span className="font-medium">Event: {location.state.eventName}</span>
              <span className="ml-4">Date: {location.state.date}</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mx-4 mt-3 px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Table Section */}
          <div className="p-4">
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <p className="mt-2 text-gray-600">Loading...</p>
              </div>
            ) : (
              <div className="overflow-auto">
                <table className="w-full border-collapse border border-gray-300" style={{ tableLayout: 'auto' }}>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Code</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Name</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Casino Amt</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Casino Comm</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Total Amount</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">My Share</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">M.App</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Net Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.length === 0 ? (
                      <tr>
                        <td className="border border-gray-300 px-3 py-2 text-center" colSpan="8">
                          {currentUserName ? `No downline data for ${currentUserName}` : 'No data available'}
                        </td>
                      </tr>
                    ) : (
                      reportData.map((item, index) => {
                        // Check if this is a Client (bottom level)
                        const isClient = item.userId && item.userId.startsWith('C');
                        
                        return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-2 text-sm">
                            {isClient ? (
                              // Client level - not clickable
                              <span className="font-medium">
                                {item.userId}
                              </span>
                            ) : (
                              // Other levels - clickable
                              <button
                                onClick={function() { handleUserClick(item.userId, item.userName); }}
                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer"
                                title="Click to view downline"
                              >
                                {item.userId}
                              </button>
                            )}
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-sm font-medium">
                            {item.userName}
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">
                            <span className={item.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {formatNumber(item.amount)}
                            </span>
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">
                            <span className={item.commission >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {formatNumber(item.commission)}
                            </span>
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">
                            <span className={item.total >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {formatNumber(item.total)}
                            </span>
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">
                            <span className={item.myShare >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {formatNumber(item.myShare)}
                            </span>
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">
                            {item.mapp ? formatNumber(item.mapp) : ''}
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">
                            <span className={item.netAmount >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {formatNumber(item.netAmount)}
                            </span>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                  <tfoot className="bg-gray-100 font-semibold">
                    <tr>
                      <td className="border border-gray-300 px-3 py-2 text-sm" colSpan="2">Total</td>
                      <td className="border border-gray-300 px-3 py-2 text-sm">
                        <span className={totals.casinoAmt >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatNumber(totals.casinoAmt)}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm">
                        <span className={totals.casinoComm >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatNumber(totals.casinoComm)}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm">
                        <span className={totals.totalAmount >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatNumber(totals.totalAmount)}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm">
                        <span className={totals.myShare >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatNumber(totals.myShare)}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm"></td>
                      <td className="border border-gray-300 px-3 py-2 text-sm">
                        <span className={totals.netAmount >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatNumber(totals.netAmount)}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default CompletedCasinoPlusMinus;